// ─── CustomerDetailPage.jsx ─────────────────────────────────────────────────
import React, { useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box, Typography, Grid, Card, CardContent, Button, Chip,
  Table, TableBody, TableCell, TableHead, TableRow, CircularProgress,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField,
  FormControl, InputLabel, Select, MenuItem, Avatar, Stack, IconButton,
  Paper, Tabs, Tab
} from '@mui/material';
import { 
  ArrowBack, Add, Person, Phone, Email, LocationOn, Delete, CloudUpload,
  Edit, Camera, Assignment, History, CalendarToday, Business
} from '@mui/icons-material';
import { customersAPI } from '../services/api';
import { StatusChip } from '../components/common/DataTable';
import { toast } from 'react-toastify';
import { useForm } from 'react-hook-form';
import {
  getCustomerDocumentUrl,
  isCustomerPhotoDocument,
  isImageCustomerDocument,
} from '../utils/customerDocuments';

const fmt = (n) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n || 0);

const InteractionDialog = ({ open, onClose, customerId, onSaved }) => {
  const { register, handleSubmit, reset } = useForm();
  const [loading, setLoading] = useState(false);
  const onSubmit = async (data) => {
    try { 
      setLoading(true); 
      await customersAPI.addInteraction(customerId, data); 
      toast.success('Interaction logged'); 
      reset(); 
      onSaved(); 
      onClose(); 
    }
    catch { toast.error('Failed'); } 
    finally { setLoading(false); }
  };
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogTitle fontWeight={700}>Log Interaction</DialogTitle>
        <DialogContent dividers>
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel>Type *</InputLabel>
            <Select {...register('interaction_type', { required: true })} label="Type *" defaultValue="call">
              {['call','email','meeting','note','follow_up'].map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
            </Select>
          </FormControl>
          <TextField fullWidth label="Subject" {...register('subject')} sx={{ mb: 2 }} />
          <TextField fullWidth label="Description *" multiline rows={3} {...register('description', { required: true })} sx={{ mb: 2 }} />
          <TextField fullWidth label="Outcome" {...register('outcome')} sx={{ mb: 2 }} />
          <TextField fullWidth label="Follow-up Date" type="date" InputLabelProps={{ shrink: true }} {...register('follow_up_date')} />
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={onClose}>Cancel</Button>
          <Button type="submit" variant="contained" disabled={loading}>{loading ? <CircularProgress size={20} /> : 'Log'}</Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export const CustomerDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [customer, setCustomer] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [interactionOpen, setInteractionOpen] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [imagePreviewOpen, setImagePreviewOpen] = useState(false);
  const [previewImage, setPreviewImage] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [documentToDelete, setDocumentToDelete] = useState(null);

  const fetchCustomer = useCallback(async () => {
    try { 
      const { data } = await customersAPI.getById(id); 
      setCustomer(data.data); 
    }
    catch { toast.error('Failed to load customer'); }
    finally { setLoading(false); }
  }, [id]);

  const fetchDocuments = useCallback(async () => {
    try {
      const { data } = await customersAPI.getDocuments(id);
      setDocuments(data.data || []);
    } catch {
    }
  }, [id]);

  const deleteDocument = async (documentId) => {
    try {
      await customersAPI.deleteDocument(id, documentId);
      setDocuments(documents.filter(doc => doc.id !== documentId));
      toast.success('Document deleted');
    } catch (error) {
      toast.error('Failed to delete document');
    }
  };

  const confirmDeleteDocument = (document) => {
    setDocumentToDelete(document);
    setDeleteConfirmOpen(true);
  };

  const cancelDelete = () => {
    setDeleteConfirmOpen(false);
    setDocumentToDelete(null);
  };

  const executeDelete = async () => {
    if (documentToDelete) {
      await deleteDocument(documentToDelete.id);
      setDeleteConfirmOpen(false);
      setDocumentToDelete(null);
    }
  };

  const openImagePreview = (imageUrl) => {
    setPreviewImage(getCustomerDocumentUrl(imageUrl));
    setImagePreviewOpen(true);
  };

  const closeImagePreview = () => {
    setImagePreviewOpen(false);
    setPreviewImage(null);
  };

  useEffect(() => { 
    fetchCustomer(); 
    fetchDocuments();
  }, [fetchCustomer, fetchDocuments]);

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}><CircularProgress /></Box>;
  if (!customer) return <Typography>Customer not found</Typography>;

  const customerPhoto = documents.find(isCustomerPhotoDocument);
  const idDocuments = documents.filter((doc) => !isCustomerPhotoDocument(doc));
  
  // Calculate real metrics from customer data
  const totalOrders = customer.recent_orders ? customer.recent_orders.length : 0;
  const totalSpent = customer.recent_orders ? 
    customer.recent_orders.reduce((sum, order) => sum + parseFloat(order.total_amount || 0), 0) : 0;

  return (
    <Box sx={{ maxWidth: '1600px', mx: 'auto', p: { xs: 2, sm: 3, md: 4 } }}>
      {/* Header */}
      <Paper sx={{ p: 3, mb: 3, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, flexWrap: 'wrap' }}>
          <Button startIcon={<ArrowBack />} onClick={() => navigate('/customers')} sx={{ color: 'white' }}>
            Back
          </Button>
          <Avatar sx={{ width: 80, height: 80, bgcolor: 'rgba(255,255,255,0.2)', fontSize: '2rem' }}>
            {customerPhoto ? (
              <img src={getCustomerDocumentUrl(customerPhoto)} alt={customer.first_name} 
                style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            ) : (
              `${customer.first_name?.[0]}${customer.last_name?.[0]}`
            )}
          </Avatar>
          <Box sx={{ flexGrow: 1, minWidth: 200 }}>
            <Typography variant="h3" fontWeight={800} gutterBottom>
              {customer.first_name} {customer.last_name}
            </Typography>
            <Typography variant="h6" sx={{ opacity: 0.9, mb: 1 }}>
              {customer.customer_code} • {customer.customer_type}
            </Typography>
            <Stack direction="row" spacing={2} alignItems="center">
              <StatusChip status={customer.is_active ? 'active' : 'inactive'} />
            </Stack>
          </Box>
          <Stack direction="row" spacing={1}>
            <Button variant="contained" startIcon={<Edit />} sx={{ bgcolor: 'rgba(255,255,255,0.2)', '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' } }}>
              Edit
            </Button>
            <Button variant="contained" startIcon={<Add />} onClick={() => setInteractionOpen(true)}>
              Log Interaction
            </Button>
          </Stack>
        </Box>
      </Paper>

      {/* Quick Stats */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6}>
          <Card sx={{ textAlign: 'center', py: 3 }}>
            <Typography variant="h3" color="primary.main" fontWeight={800}>{totalOrders}</Typography>
            <Typography variant="body1" color="text.secondary">Total Orders</Typography>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Card sx={{ textAlign: 'center', py: 3 }}>
            <Typography variant="h3" color="success.main" fontWeight={800}>{fmt(totalSpent)}</Typography>
            <Typography variant="body1" color="text.secondary">Total Spent</Typography>
          </Card>
        </Grid>
      </Grid>

      {/* Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)} sx={{ borderBottom: '1px solid #e0e0e0' }}>
          <Tab label="Overview" />
          <Tab label="Documents" />
          <Tab label="Orders" />
          <Tab label="Interactions" />
        </Tabs>
      </Paper>

      {/* Tab Content */}
      <Box sx={{ display: activeTab === 0 ? 'block' : 'none' }}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" fontWeight={700} gutterBottom>Contact Information</Typography>
                <Stack spacing={2}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Email color="primary" />
                    <Box>
                      <Typography variant="body2" color="text.secondary">Email</Typography>
                      <Typography variant="body1">{customer.email || 'N/A'}</Typography>
                    </Box>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Phone color="primary" />
                    <Box>
                      <Typography variant="body2" color="text.secondary">Phone</Typography>
                      <Typography variant="body1">{customer.phone || 'N/A'}</Typography>
                    </Box>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <LocationOn color="primary" />
                    <Box>
                      <Typography variant="body2" color="text.secondary">Location</Typography>
                      <Typography variant="body1">
                        {[customer.city, customer.country].filter(Boolean).join(', ') || 'N/A'}
                      </Typography>
                    </Box>
                  </Box>
                  {customer.company_name && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Business color="primary" />
                      <Box>
                        <Typography variant="body2" color="text.secondary">Company</Typography>
                        <Typography variant="body1">{customer.company_name}</Typography>
                      </Box>
                    </Box>
                  )}
                </Stack>
              </CardContent>
            </Card>

            <Card>
              <CardContent>
                <Typography variant="h6" fontWeight={700} gutterBottom>Customer Details</Typography>
                <Stack spacing={2}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2" color="text.secondary">Customer Type</Typography>
                    <Typography variant="body2" fontWeight={600}>{customer.customer_type}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2" color="text.secondary">Source</Typography>
                    <Typography variant="body2" fontWeight={600}>{customer.source || 'N/A'}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2" color="text.secondary">Created</Typography>
                    <Typography variant="body2" fontWeight={600}>
                      {new Date(customer.created_at).toLocaleDateString()}
                    </Typography>
                  </Box>
                  {customer.national_id && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2" color="text.secondary">National ID</Typography>
                      <Typography variant="body2" fontWeight={600}>{customer.national_id}</Typography>
                    </Box>
                  )}
                </Stack>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card sx={{ height: '100%' }}>
              <CardContent>
                <Typography variant="h6" fontWeight={700} gutterBottom>Recent Activity</Typography>
                {customer.interactions && customer.interactions.length > 0 ? (
                  <Stack spacing={2}>
                    {customer.interactions.slice(0, 5).map((interaction) => (
                      <Box key={interaction.id} sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                        <Box sx={{ 
                          width: 40, 
                          height: 40, 
                          borderRadius: '50%', 
                          bgcolor: 'primary.main', 
                          display: 'flex', 
                          alignItems: 'center', 
                          justifyContent: 'center',
                          color: 'white',
                          flexShrink: 0
                        }}>
                          {interaction.interaction_type === 'call' && <Phone sx={{ fontSize: 20 }} />}
                          {interaction.interaction_type === 'email' && <Email sx={{ fontSize: 20 }} />}
                          {interaction.interaction_type === 'meeting' && <Person sx={{ fontSize: 20 }} />}
                          {interaction.interaction_type === 'note' && <Assignment sx={{ fontSize: 20 }} />}
                          {interaction.interaction_type === 'follow_up' && <CalendarToday sx={{ fontSize: 20 }} />}
                        </Box>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography variant="body2" fontWeight={600}>
                            {interaction.interaction_type.replace('_', ' ').toUpperCase()}
                          </Typography>
                          {interaction.subject && (
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                              {interaction.subject}
                            </Typography>
                          )}
                          <Typography variant="caption" color="text.secondary">
                            {new Date(interaction.created_at).toLocaleDateString()}
                          </Typography>
                        </Box>
                      </Box>
                    ))}
                  </Stack>
                ) : (
                  <Box sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>
                    <History sx={{ fontSize: 48, mb: 2 }} />
                    <Typography variant="body1">No interactions yet</Typography>
                    <Typography variant="caption">Start logging customer interactions</Typography>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>

      <Box sx={{ display: activeTab === 1 ? 'block' : 'none' }}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" fontWeight={700} gutterBottom>Customer Photo</Typography>
                {customerPhoto ? (
                  <Box sx={{ textAlign: 'center' }}>
                    <img 
                      src={getCustomerDocumentUrl(customerPhoto)}
                      alt={`${customer.first_name} ${customer.last_name}`}
                      style={{ 
                        maxWidth: '100%', 
                        maxHeight: '300px', 
                        objectFit: 'contain', 
                        borderRadius: '12px',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                        cursor: 'pointer',
                        transition: 'transform 0.2s ease-in-out'
                      }}
                      onClick={() => openImagePreview(customerPhoto)}
                      onMouseOver={(e) => e.target.style.transform = 'scale(1.02)'}
                      onMouseOut={(e) => e.target.style.transform = 'scale(1)'}
                    />
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="body2" color="text.secondary">
                        Uploaded: {new Date(customerPhoto.created_at).toLocaleDateString()}
                      </Typography>
                      <Typography variant="caption" color="primary" sx={{ cursor: 'pointer' }}>
                        Click to enlarge
                      </Typography>
                    </Box>
                  </Box>
                ) : (
                  <Box sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>
                    <Camera sx={{ fontSize: 64, mb: 2 }} />
                    <Typography variant="body1">No customer photo</Typography>
                    <Typography variant="caption">Upload a photo during customer creation or edit</Typography>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" fontWeight={700} gutterBottom>ID Documents</Typography>
                {idDocuments.length > 0 ? (
                  <Stack spacing={2}>
                    {idDocuments.map((doc) => (
                      <Card key={doc.id} sx={{ p: 2, border: '1px solid #e0e0e0' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          {isImageCustomerDocument(doc) ? (
                            <img 
                              src={getCustomerDocumentUrl(doc)}
                              alt={doc.file_name}
                              style={{ 
                                width: '80px', 
                                height: '80px', 
                                objectFit: 'cover', 
                                borderRadius: '8px',
                                cursor: 'pointer',
                                transition: 'transform 0.2s ease-in-out'
                              }}
                              onClick={() => openImagePreview(doc)}
                              onMouseOver={(e) => e.target.style.transform = 'scale(1.05)'}
                              onMouseOut={(e) => e.target.style.transform = 'scale(1)'}
                            />
                          ) : (
                            <Box sx={{ 
                              width: '80px', 
                              height: '80px', 
                              bgcolor: 'grey.100', 
                              borderRadius: '8px', 
                              display: 'flex', 
                              alignItems: 'center', 
                              justifyContent: 'center'
                            }}>
                              <Typography variant="caption" color="text.secondary" fontWeight={600}>PDF</Typography>
                            </Box>
                          )}
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography variant="body1" fontWeight={600} noWrap>
                              {doc.file_name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" display="block">
                              {doc.document_type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {(doc.file_size / 1024).toFixed(1)} KB • {new Date(doc.created_at).toLocaleDateString()}
                            </Typography>
                            {isImageCustomerDocument(doc) && (
                              <Typography variant="caption" color="primary" sx={{ cursor: 'pointer' }}>
                                Click to enlarge
                              </Typography>
                            )}
                          </Box>
                          <Button
                            size="small"
                            variant="outlined"
                            href={getCustomerDocumentUrl(doc)}
                            target="_blank"
                            rel="noreferrer"
                          >
                            Open
                          </Button>
                          <IconButton size="small" color="error" onClick={() => confirmDeleteDocument(doc)}>
                            <Delete />
                          </IconButton>
                        </Box>
                      </Card>
                    ))}
                  </Stack>
                ) : (
                  <Box sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>
                    <CloudUpload sx={{ fontSize: 64, mb: 2 }} />
                    <Typography variant="body1">No ID documents uploaded</Typography>
                    <Typography variant="caption">Upload ID cards, licenses, or passports</Typography>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>

      <Box sx={{ display: activeTab === 2 ? 'block' : 'none' }}>
        <Card>
          <CardContent>
            <Typography variant="h6" fontWeight={700} gutterBottom>Order History</Typography>
            {customer.recent_orders && customer.recent_orders.length > 0 ? (
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>Order #</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Amount</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {customer.recent_orders.map((order) => (
                    <TableRow key={order.order_number} hover>
                      <TableCell sx={{ fontFamily: 'monospace', fontWeight: 600 }}>
                        {order.order_number}
                      </TableCell>
                      <TableCell>{new Date(order.order_date).toLocaleDateString()}</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>{fmt(order.total_amount)}</TableCell>
                      <TableCell><StatusChip status={order.status} /></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <Box sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>
                <Assignment sx={{ fontSize: 64, mb: 2 }} />
                <Typography variant="body1">No orders yet</Typography>
                <Typography variant="caption">This customer hasn't placed any orders</Typography>
              </Box>
            )}
          </CardContent>
        </Card>
      </Box>

      <Box sx={{ display: activeTab === 3 ? 'block' : 'none' }}>
        <Card>
          <CardContent>
            <Typography variant="h6" fontWeight={700} gutterBottom>Interaction History</Typography>
            {customer.interactions && customer.interactions.length > 0 ? (
              <Stack spacing={2}>
                {customer.interactions.map((interaction) => (
                  <Card key={interaction.id} sx={{ p: 2, border: '1px solid #f0f0f0' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                      <Chip 
                        label={interaction.interaction_type.replace('_', ' ').toUpperCase()} 
                        size="small" 
                        color="primary"
                      />
                      <Typography variant="caption" color="text.secondary">
                        {new Date(interaction.created_at).toLocaleDateString()}
                      </Typography>
                    </Box>
                    {interaction.subject && (
                      <Typography variant="body1" fontWeight={600} sx={{ mb: 0.5 }}>
                        {interaction.subject}
                      </Typography>
                    )}
                    {interaction.description && (
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        {interaction.description}
                      </Typography>
                    )}
                    {interaction.outcome && (
                      <Box sx={{ mt: 1 }}>
                        <Typography variant="caption" color="text.secondary">Outcome:</Typography>
                        <Typography variant="body2">{interaction.outcome}</Typography>
                      </Box>
                    )}
                  </Card>
                ))}
              </Stack>
            ) : (
              <Box sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>
                <History sx={{ fontSize: 64, mb: 2 }} />
                <Typography variant="body1">No interactions logged yet</Typography>
                <Typography variant="caption">Start tracking customer communications</Typography>
              </Box>
            )}
          </CardContent>
        </Card>
      </Box>

      <InteractionDialog open={interactionOpen} onClose={() => setInteractionOpen(false)} customerId={id} onSaved={() => { fetchCustomer(); fetchDocuments(); }} />
      
      {/* Image Preview Modal */}
      <Dialog open={imagePreviewOpen} onClose={closeImagePreview} maxWidth="lg" fullWidth>
        <DialogContent sx={{ p: 0, position: 'relative' }}>
          <IconButton
            sx={{ position: 'absolute', top: 8, right: 8, zIndex: 1, bgcolor: 'rgba(0,0,0,0.5)', color: 'white' }}
            onClick={closeImagePreview}
          >
            <Delete />
          </IconButton>
          {previewImage && (
            <img 
              src={previewImage} 
              alt="Document Preview"
              style={{ 
                width: '100%', 
                height: 'auto', 
                maxHeight: '80vh', 
                objectFit: 'contain',
                display: 'block'
              }}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirmOpen} onClose={cancelDelete} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Delete color="error" />
          Confirm Delete
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Are you sure you want to delete this document?
          </Typography>
          {documentToDelete && (
            <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
              <Typography variant="body2" fontWeight={600}>
                {documentToDelete.file_name}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {documentToDelete.document_type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())} • 
                {(documentToDelete.file_size / 1024).toFixed(1)} KB
              </Typography>
            </Box>
          )}
          <Typography variant="body2" color="error" sx={{ mt: 2 }}>
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={cancelDelete}>Cancel</Button>
          <Button 
            onClick={executeDelete} 
            variant="contained" 
            color="error"
            startIcon={<Delete />}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CustomerDetailPage;
